import CloseIcon from "@mui/icons-material/Close";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	FormControl,
	InputLabel,
	MenuItem,
	Modal,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import { useCallback, useEffect, useId, useState } from "react";
import { useForm } from "react-hook-form";
import { ADMIN_ROLES } from "@/config/constants";
import { DualButton } from "@/shared/components/common";
import { useCognitoOperations, useSnackbar } from "@/shared/hooks";
import type { AdminEditFormData } from "@/shared/types";

interface EditDialogProps {
	open: boolean;
	handleClose: () => void;
	initialData: AdminEditFormData;
	onEditUser: () => void | Promise<void>;
}

export function EditAdminsUserDialog({
	open,
	handleClose,
	initialData,
	onEditUser,
}: EditDialogProps): React.ReactElement {
	const { deleteUser, updateUserAttributes } = useCognitoOperations();
	const { showSuccess, showError } = useSnackbar();
	const [loading, setLoading] = useState(false);
	const [openConfirm, setOpenConfirm] = useState(false);
	const { register, handleSubmit, reset } = useForm({
		defaultValues: initialData,
	});
	const usergroupId = useId();

	useEffect(() => {
		reset(initialData);
	}, [initialData, reset]);

	const handleCancel = useCallback(() => {
		setOpenConfirm(false);
		handleClose();
	}, [handleClose]);

	const handleSave = useCallback(
		async (data: { email: string; department: string; role: string }) => {
			try {
				setLoading(true);
				await updateUserAttributes(data);
				showSuccess(`${initialData.email} のユーザー情報を更新しました。`);
				onEditUser();
				handleClose();
			} catch (error) {
				showError("ユーザー情報の更新に失敗しました。");
				console.error("Failed to update user:", error);
			} finally {
				setLoading(false);
			}
		},
		[updateUserAttributes, initialData.email, onEditUser, handleClose, showSuccess, showError],
	);

	const handleDeleteConfirmed = useCallback(async () => {
		try {
			setLoading(true);
			await deleteUser(initialData.email);
			showSuccess(`${initialData.email} を削除しました。`);
			onEditUser();
			setOpenConfirm(false);
			handleClose();
		} catch (error) {
			showError("ユーザーの削除に失敗しました。");
			console.error("Failed to delete user:", error);
		} finally {
			setLoading(false);
		}
	}, [deleteUser, initialData.email, onEditUser, handleClose, showSuccess, showError]);

	return (
		<Box>
			<Modal open={open} onClose={handleClose}>
				<Box
					component="form"
					onSubmit={handleSubmit(handleSave)}
					sx={{
						position: "absolute",
						top: "50%",
						left: "50%",
						transform: "translate(-50%, -50%)",
						width: 400,
						bgcolor: "background.paper",
						boxShadow: 24,
						p: 3,
						borderRadius: 2,
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						gap: 2,
					}}
				>
					<IconButton
						onClick={handleCancel}
						sx={{
							position: "absolute",
							top: 8,
							right: 8,
							color: (theme) => theme.palette.grey[500],
						}}
					>
						<CloseIcon />
					</IconButton>
					<Box sx={{ width: "100%", display: "flex", justifyContent: "left", mb: 1 }}>
						<Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
							ユーザー情報編集
						</Typography>
					</Box>
					<TextField
						label="メールアドレス"
						value={initialData.email}
						disabled
						fullWidth
						sx={{ mb: 2 }}
					/>
					<FormControl fullWidth sx={{ mb: 2 }}>
						<InputLabel id={`${usergroupId}-label`}>権限</InputLabel>
						<Select
							labelId={`${usergroupId}-label`}
							defaultValue={initialData.role}
							{...register("role")}
						>
							{ADMIN_ROLES.map((role) => (
								<MenuItem key={role} value={role}>
									{role}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<TextField
						label="所属"
						defaultValue={initialData.department}
						{...register("department")}
						sx={{ width: "100%" }}
						multiline
						minRows={3}
						slotProps={{
							input: {
								sx: {
									padding: "0px",
								},
							},
						}}
					/>
					<Box sx={{ width: "100%", display: "flex", justifyContent: "center", mt: 1, gap: 6 }}>
						<Button
							variant="contained"
							color="error"
							type="button"
							onClick={() => setOpenConfirm(true)}
							sx={{ width: 120, padding: "6px 0px" }}
						>
							ユーザー削除
						</Button>
						<Button
							variant="contained"
							color="primary"
							type="submit"
							sx={{ width: 120, padding: "6px 0px" }}
						>
							保存
						</Button>
					</Box>
				</Box>
			</Modal>

			<Dialog open={openConfirm} onClose={() => setOpenConfirm(false)}>
				<DialogTitle>ユーザー削除</DialogTitle>
				<DialogContent>
					<DialogContentText>
						選択したユーザーを削除しようとしています。
						<br />
						削除されたユーザーは復元できません。よろしいですか？
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Box sx={{ display: "flex", justifyContent: "center", width: "100%", mb: 2 }}>
						<DualButton
							cancelText="キャンセル"
							submitText="削除"
							onCancel={handleCancel}
							onSubmit={handleDeleteConfirmed}
							loading={loading}
						/>
					</Box>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
